<script setup lang="ts">
/**
 * The signed-in person's own page: what everyone else sees them as, their
 * password, and how the board opens for them. Reached from the status dot,
 * and the only place to sign out.
 *
 * The settings save as they change, like the same switches in the pane header
 * (they are the same refs); name and password are forms, because a half-typed
 * name must not be broadcast to the whole team a keystroke at a time.
 */
import { computed, ref, watch } from "vue";
import { api } from "../lib/api.js";
import { toast } from "../lib/toast.js";
import { disableNotifications } from "../lib/notifications.js";
import { clearCachedGraphs } from "../lib/gitGraphCache.js";
import { forgetChatTrees } from "../lib/chatTrees.js";
import { clearKept } from "../lib/tmuxCache.js";
import { useBoard } from "../stores/board.js";
import { useViewport, VIEWS, VIEW_LABELS } from "../stores/viewport.js";
import type { User } from "@shared/types.js";

const board = useBoard();
const viewport = useViewport();

/** The same hues `pickColor()` hands out on sign-up. */
const SWATCHES = [8, 32, 48, 96, 150, 190, 210, 260, 290, 330].map((h) => `hsl(${h} 65% 55%)`);

const initials = (name: string): string =>
  name.split(/\s+/).map((p) => p[0] ?? "").join("").slice(0, 2) || name.slice(0, 2);

const displayName = ref(board.me?.displayName ?? "");
const color = ref(board.me?.color ?? SWATCHES[0]!);
const profileProblem = ref("");
const savingProfile = ref(false);

// A rename from another tab lands here too, unless this form has edits.
watch(
  () => board.me,
  (me, was) => {
    if (!me) return;
    if (displayName.value === was?.displayName) displayName.value = me.displayName;
    if (color.value === was?.color) color.value = me.color;
  },
);

const profileDirty = computed(
  () => displayName.value.trim() !== board.me?.displayName || color.value !== board.me?.color,
);
/** `<input type=color>` only speaks #rrggbb. */
const pickerValue = computed(() => (/^#[0-9a-f]{6}$/i.test(color.value) ? color.value : "#888888"));

async function saveProfile(): Promise<void> {
  profileProblem.value = "";
  savingProfile.value = true;
  try {
    const { user } = await api.patch<{ user: User }>("/api/me", {
      displayName: displayName.value,
      color: color.value,
    });
    board.me = user;
    board.users.set(user.id, user);
    displayName.value = user.displayName;
    toast("Profile saved");
  } catch (err) {
    profileProblem.value = (err as Error).message;
  } finally {
    savingProfile.value = false;
  }
}

const current = ref("");
const next = ref("");
const confirm = ref("");
const passwordProblem = ref("");
const savingPassword = ref(false);

const mismatch = computed(() => confirm.value !== "" && confirm.value !== next.value);

async function changePassword(): Promise<void> {
  passwordProblem.value = "";
  if (next.value.length < 8) {
    passwordProblem.value = "New password must be at least 8 characters.";
    return;
  }
  if (next.value !== confirm.value) {
    passwordProblem.value = "The two new passwords don't match.";
    return;
  }
  savingPassword.value = true;
  try {
    await api.post("/api/me/password", { current: current.value, next: next.value });
    current.value = next.value = confirm.value = "";
    toast("Password changed. You've been signed out everywhere else.", 4000);
  } catch (err) {
    passwordProblem.value = (err as Error).message;
  } finally {
    savingPassword.value = false;
  }
}

async function signOut(): Promise<void> {
  // Before the session goes: a shared browser must not go on telling the next
  // person what was said to this one.
  await disableNotifications().catch(() => undefined);
  clearCachedGraphs();
  forgetChatTrees();
  clearKept();
  await api.post("/api/logout");
  board.stop();
}
</script>

<template>
  <section class="profile" aria-label="Your profile">
    <div class="profile-inner">
      <header class="profile-head">
        <span class="avatar profile-avatar" :style="{ background: color }">
          {{ initials(displayName.trim() || board.me?.username || "?") }}
        </span>
        <div>
          <h2>{{ board.me?.displayName }}</h2>
          <div class="profile-sub">
            @{{ board.me?.username }}<span v-if="board.me?.isAdmin"> · admin</span>
            · <span class="dot" :class="board.status"></span> {{ board.status === "open" ? "connected" : board.status }}
          </div>
        </div>
        <button class="btn profile-signout" type="button" @click="signOut">Sign out</button>
      </header>

      <form class="money-card profile-card" @submit.prevent="saveProfile">
        <h3>You</h3>
        <label class="money-field">
          <span>Name</span>
          <input v-model="displayName" maxlength="64" autocomplete="name" />
        </label>
        <div class="money-field">
          <span>Colour — how you appear on everyone's board</span>
          <div class="profile-swatches">
            <button
              v-for="s in SWATCHES"
              :key="s"
              type="button"
              class="profile-swatch"
              :class="{ on: color === s }"
              :style="{ background: s }"
              :aria-label="`Use ${s}`"
              :aria-pressed="color === s"
              @click="color = s"
            ></button>
            <label class="profile-swatch profile-custom" :class="{ on: !SWATCHES.includes(color) }" title="Pick any colour">
              <input type="color" :value="pickerValue" aria-label="Custom colour" @input="color = ($event.target as HTMLInputElement).value" />
            </label>
          </div>
        </div>
        <p v-if="profileProblem" class="money-problem">{{ profileProblem }}</p>
        <div class="money-actions">
          <button class="btn primary" type="submit" :disabled="!profileDirty || savingProfile">Save</button>
        </div>
      </form>

      <form class="money-card profile-card" @submit.prevent="changePassword">
        <h3>Password</h3>
        <!-- For password managers: which account this is. -->
        <input type="text" :value="board.me?.username" autocomplete="username" hidden readonly />
        <label class="money-field">
          <span>Current password</span>
          <input v-model="current" type="password" autocomplete="current-password" required />
        </label>
        <label class="money-field">
          <span>New password</span>
          <input v-model="next" type="password" autocomplete="new-password" minlength="8" required />
        </label>
        <label class="money-field">
          <span>New password again</span>
          <input v-model="confirm" type="password" autocomplete="new-password" :aria-invalid="mismatch" required />
        </label>
        <p v-if="passwordProblem" class="money-problem">{{ passwordProblem }}</p>
        <p v-else class="money-hint">Changing it signs you out on every other device.</p>
        <div class="money-actions">
          <button class="btn primary" type="submit" :disabled="savingPassword || !current || !next || !confirm">
            Change password
          </button>
        </div>
      </form>

      <div class="money-card profile-card">
        <h3>Settings</h3>
        <p class="money-hint">Saved to your account as you change them, so they follow you to other devices.</p>
        <label class="money-field">
          <span>Open on</span>
          <select v-model="viewport.startView">
            <option v-for="v in VIEWS.filter((v) => v !== 'team')" :key="v" :value="v">{{ VIEW_LABELS[v] }}</option>
          </select>
        </label>
        <label class="money-field">
          <span>Theme</span>
          <select v-model="viewport.theme">
            <option value="system">same as this device</option>
            <option value="light">light</option>
            <option value="dark">dark</option>
          </select>
        </label>
        <label class="money-field">
          <span>Order tasks</span>
          <select v-model="viewport.sortBy">
            <option value="manual">manual order</option>
            <option value="urgency">by urgency</option>
          </select>
        </label>
        <label class="profile-check">
          <input v-model="viewport.onlyMine" type="checkbox" />
          Show only my tasks on the board (and the tasks they sit under)
        </label>
        <label class="profile-check">
          <input v-model="viewport.hideDone" type="checkbox" />
          Hide finished tasks on the board
        </label>
        <label class="profile-check">
          <input v-model="viewport.hideDoneSubtasks" type="checkbox" />
          Hide finished sub-tasks in a task's drawer
        </label>
        <label class="profile-check">
          <input v-model="viewport.commentsOnly" type="checkbox" />
          Show only comments in a task's timeline
        </label>
      </div>

    </div>
  </section>
</template>
